import { portalErrorMessage } from "@/lib/portalErrors";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import type { Database } from "@/integrations/supabase/types";

type ApplicationRow = Database["public"]["Tables"]["applications"]["Row"];
type ApplicationInsert = Database["public"]["Tables"]["applications"]["Insert"];
type ApplicationUpdate = Database["public"]["Tables"]["applications"]["Update"];
type ApplicantDetailRow = Database["public"]["Tables"]["applicant_details"]["Row"];
type ApplicantDetailUpsert = Database["public"]["Tables"]["applicant_details"]["Insert"];

export type Application = ApplicationRow;
export type ApplicantDetails = ApplicantDetailRow;

export function useApplications() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["applications", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useApplication(id: string | undefined) {
  return useQuery({
    queryKey: ["application", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });
}

export function useApplicantDetails(applicationId: string | undefined) {
  return useQuery({
    queryKey: ["applicant_details", applicationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applicant_details")
        .select("*")
        .eq("application_id", applicationId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!applicationId,
  });
}

export function useCreateApplication() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (membershipPackage: ApplicationInsert["membership_package"]) => {
      // One open application per person: resume the existing draft instead of
      // creating a second one (survey + dashboard used to create duplicates).
      const { data: open, error: openError } = await supabase
        .from("applications")
        .select("*")
        .eq("user_id", user!.id)
        .or("is_next_member.is.null,is_next_member.eq.false")
        .not("status", "in", "(cancelled,rejected)")
        .order("created_at", { ascending: false })
        .limit(1);
      if (openError) throw openError;
      const existing = open?.[0];
      if (existing?.status === "draft") return existing;
      if (existing) throw new Error("You already have an application in progress.");

      const { data, error } = await supabase
        .from("applications")
        .insert({ user_id: user!.id, membership_package: membershipPackage })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

export function useCreateNextMemberApplication() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (coreApplicationId: string) => {
      // Fetch core application to get package
      const { data: coreApp, error: coreErr } = await supabase
        .from("applications")
        .select("membership_package")
        .eq("id", coreApplicationId)
        .single();
      if (coreErr || !coreApp) throw new Error("Core application not found");

      const eligiblePackages = ["platinum", "diamond", "reserve"];
      if (!eligiblePackages.includes(coreApp.membership_package)) {
        throw new Error("This package does not allow next member applications");
      }

      const { data, error } = await supabase
        .from("applications")
        .insert({
          user_id: user!.id,
          membership_package: coreApp.membership_package,
          is_next_member: true,
          core_application_id: coreApplicationId,
          current_step: 1,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

export function useCoreApplication(coreApplicationId: string | null | undefined) {
  return useQuery({
    queryKey: ["application", coreApplicationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .eq("id", coreApplicationId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!coreApplicationId,
  });
}

export function useUpdateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: ApplicationUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from("applications")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onError: (error, variables) => {
      portalErrorMessage(error, { operation: "save-application", applicationId: variables.id });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application", data.id] });
      queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

export function useUpsertApplicantDetails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (details: ApplicantDetailUpsert) => {
      // Sanitize date fields: empty string "" is invalid for Postgres date columns → use null
      // IMPORTANT: only include date fields in the sanitized object if they were explicitly passed
      // (undefined means "not in this payload" — don't overwrite existing DB values with null)
      const sanitized: any = { ...details };
      if ("passport_expiry_date" in details)
        sanitized.passport_expiry_date = (details as any).passport_expiry_date || null;
      if ("passport_issued_date" in details)
        sanitized.passport_issued_date = (details as any).passport_issued_date || null;
      if ("visa_expiry" in details)
        sanitized.visa_expiry = (details as any).visa_expiry || null;
      if ("stamp_expiry" in details)
        sanitized.stamp_expiry = (details as any).stamp_expiry || null;
      if ("date_of_birth" in details)
        sanitized.date_of_birth = (details as any).date_of_birth || null;

      const { data, error } = await supabase
        .from("applicant_details")
        .upsert(sanitized, { onConflict: "application_id" })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onError: (error, variables) => {
      portalErrorMessage(error, { operation: "save-applicant-details", applicationId: variables.application_id });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["applicant_details", data.application_id] });
    },
  });
}
